import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

const STEPS = ['Personal', 'Religion', 'Education & Job', 'Family', 'Partner Preferences', 'Photos'];

const DISTRICTS_AP = ['Anantapur','Chittoor','East Godavari','Guntur','Krishna','Kurnool','Nellore','Prakasam','Srikakulam','Visakhapatnam','Vizianagaram','West Godavari','YSR Kadapa'];
const DISTRICTS_TS = ['Adilabad','Bhadradri Kothagudem','Hyderabad','Jagtial','Jangaon','Kamareddy','Karimnagar','Khammam','Mancherial','Medak','Medchal','Nalgonda','Nizamabad','Ranga Reddy','Sangareddy','Siddipet','Suryapet','Vikarabad','Warangal Rural','Warangal Urban'];

const DENOMINATIONS = [
  { value: 'catholic',              label: 'Catholic' },
  { value: 'csi',                   label: 'CSI (Church of South India)' },
  { value: 'cni',                   label: 'CNI (Church of North India)' },
  { value: 'pentecostal',           label: 'Pentecostal' },
  { value: 'assemblies_of_god',     label: 'Assemblies of God' },
  { value: 'seventh_day_adventist', label: 'Seventh Day Adventist' },
  { value: 'methodist',             label: 'Methodist' },
  { value: 'lutheran',              label: 'Lutheran' },
  { value: 'brethren',              label: 'Brethren' },
  { value: 'baptist',               label: 'Baptist' },
  { value: 'church_of_god',         label: 'Church of God' },
  { value: 'iec',                   label: 'IEC' },
  { value: 'other',                 label: 'Other' },
];

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

// Hostinger is used for persistent photo storage
const HOSTINGER_URL    = process.env.REACT_APP_HOSTINGER_URL || 'https://holyweds.com';
const UPLOAD_SECRET    = process.env.REACT_APP_UPLOAD_SECRET || 'cm_photo_upload_2024_secret';

export default function ProfileSetupPage() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [partnerPrefs, setPartnerPrefs] = useState({
    min_age: 21,
    max_age: 35,
    denomination: '',
    district: '',
    about_partner: '',
  });

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
    shouldUnregister: true,
  });
  const navigate = useNavigate();
  const state = watch('state');
  const districts = state === 'Telangana' ? DISTRICTS_TS : DISTRICTS_AP;

  // Load existing profile data to pre-fill the form
  useEffect(() => {
    api.get('/profiles/me').then(({ data }) => {
      const p = data.profile;
      if (!p) return;
      reset({
        first_name:         p.first_name         || '',
        last_name:          p.last_name          || '',
        date_of_birth:      p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
        height_cm:          p.height_cm          || '',
        complexion:         p.complexion         || '',
        marital_status:     p.marital_status     || 'never_married',
        state:              p.state              || 'Andhra Pradesh',
        district:           p.district           || '',
        city:               p.city               || '',
        about_me:           p.about_me           || '',
        denomination:       p.denomination       || '',
        sub_denomination:   p.sub_denomination   || '',
        education_level:    p.education_level    || '',
        education_field:    p.education_field    || '',
        college:            p.college            || '',
        occupation_type:    p.occupation_type    || 'employed',
        job_title:          p.job_title          || '',
        employer:           p.employer           || '',
        annual_income:      p.annual_income      || '',
        father_name:        p.father_name        || '',
        father_occupation:  p.father_occupation  || '',
        mother_name:        p.mother_name        || '',
        mother_occupation:  p.mother_occupation  || '',
        brothers:           p.brothers           ?? 0,
        brothers_married:   p.brothers_married   ?? 0,
        sisters:            p.sisters            ?? 0,
        sisters_married:    p.sisters_married    ?? 0,
        family_type:        p.family_type        || '',
        family_status:      p.family_status      || '',
      });
      // photos is now [{id, photo_url}] from backend
      setExistingPhotos(p.photos || []);
      if (p.min_age || p.max_age || p.about_partner) {
        setPartnerPrefs({
          min_age:      p.min_age      || 21,
          max_age:      p.max_age      || 35,
          denomination: p.partner_denomination || '',
          district:     p.partner_district     || '',
          about_partner: p.about_partner       || '',
        });
      }
    }).catch(() => {});
  }, [reset]);

  const saveStep = async (data) => {
    setSaving(true);
    try {
      if (step < 4) {
        await api.put('/profiles/me', data);
        toast.success('Saved! ✓');
        setStep(s => s + 1);
      } else if (step === 4) {
        await api.put('/profiles/me/preferences', partnerPrefs);
        toast.success('Preferences saved! ✓');
        setStep(s => s + 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (photo) => {
    // photo = { id, photo_url }
    if (!window.confirm('Delete this photo?')) return;
    try {
      // Step 1: Remove from Railway DB
      const res = await api.delete(`/profiles/me/photos/${photo.id}`);

      // Step 2: Delete the actual file from Hostinger
      const photoUrl = res.data.photo_url || photo.photo_url;
      if (photoUrl && photoUrl.startsWith('http')) {
        await fetch(`${HOSTINGER_URL}/delete_photo.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Upload-Secret': UPLOAD_SECRET },
          body: JSON.stringify({ photo_url: photoUrl }),
        });
      }

      setExistingPhotos(prev => prev.filter(p => p.id !== photo.id));
      toast.success('Photo deleted');
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  const uploadPhotos = async () => {
    if (!photos.length) { navigate('/dashboard'); return; }
    setSaving(true);
    try {
      // Step 1: Upload files to Hostinger (persistent storage)
      // userId from the stored user object (robust — avoids base64url JWT decode failures)
      let userId = '';
      try {
        userId = JSON.parse(localStorage.getItem('cm_user') || '{}').userId || '';
      } catch (_) {}

      const formData = new FormData();
      photos.forEach(f => formData.append('photos[]', f));
      formData.append('userId', userId);

      const hostRes = await fetch(`${HOSTINGER_URL}/upload_photo.php`, {
        method: 'POST',
        headers: { 'X-Upload-Secret': UPLOAD_SECRET },
        body: formData,
      });
      const hostData = await hostRes.json();
      if (!hostData.success) throw new Error(hostData.message || 'Hostinger upload failed');

      // Step 2: Save the Hostinger URLs to the Railway database
      await api.post('/profiles/me/photo-urls', { urls: hostData.urls });

      toast.success('Photos uploaded! Profile is ready! 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const errCls   = 'text-red-500 text-xs mt-1';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                i < step ? 'bg-primary text-white' :
                i === step ? 'bg-primary text-white ring-4 ring-primary/20' :
                'bg-gray-200 text-gray-500'
              }`}>
                {i < step ? '✓' : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-1 rounded ${i < step ? 'bg-primary' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="card p-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">
            Step {step + 1} of {STEPS.length}: {STEPS[step]}
          </h2>
          <p className="text-sm text-gray-500 mb-6 flex items-center gap-1">Fill in your details to find your perfect match <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 rounded object-cover inline-block" /></p>

          {/* Step 4 (Partner Preferences) uses controlled state, not react-hook-form */}
          {step === 4 ? (
            <form onSubmit={e => { e.preventDefault(); saveStep({}); }}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Partner Age From</label>
                    <input
                      type="number" min="18" max="60"
                      className={inputCls}
                      value={partnerPrefs.min_age}
                      onChange={e => setPartnerPrefs(p => ({ ...p, min_age: parseInt(e.target.value) || 18 }))}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Partner Age To</label>
                    <input
                      type="number" min="18" max="60"
                      className={inputCls}
                      value={partnerPrefs.max_age}
                      onChange={e => setPartnerPrefs(p => ({ ...p, max_age: parseInt(e.target.value) || 60 }))}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Preferred Denomination</label>
                  <select
                    className={inputCls}
                    value={partnerPrefs.denomination}
                    onChange={e => setPartnerPrefs(p => ({ ...p, denomination: e.target.value }))}
                  >
                    <option value="">Any Christian</option>
                    {DENOMINATIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Preferred District (optional)</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="e.g. Hyderabad, Guntur…"
                    value={partnerPrefs.district}
                    onChange={e => setPartnerPrefs(p => ({ ...p, district: e.target.value }))}
                  />
                </div>

                <div>
                  <label className={labelCls}>About Partner Expectations</label>
                  <textarea
                    rows={4}
                    className={inputCls}
                    placeholder="Describe qualities you're looking for…"
                    value={partnerPrefs.about_partner}
                    onChange={e => setPartnerPrefs(p => ({ ...p, about_partner: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(s => s - 1)}
                  className="btn-outline flex-1 py-3">← Back</button>
                <button type="submit" disabled={saving}
                  className="btn-primary flex-1 py-3">
                  {saving ? 'Saving…' : 'Save & Continue →'}
                </button>
              </div>
            </form>
          ) : step === 5 ? (
            /* Photos step — no react-hook-form */
            <div>
              {/* Existing photos with delete button */}
              {existingPhotos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Current Photos ({existingPhotos.length})
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    {existingPhotos.map((photo) => {
                      // photo = { id, photo_url }
                      const url = photo.photo_url || photo;
                      const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
                      return (
                        <div key={photo.id || url} className="relative group">
                          <img
                            src={fullUrl} alt=""
                            className="w-24 h-24 object-cover rounded-xl border border-gray-200"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <button
                            type="button"
                            onClick={() => deletePhoto(photo)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center shadow hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete photo"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Hover over a photo to delete it</p>
                </div>
              )}

              <label className="block w-full border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={e => setPhotos(Array.from(e.target.files))} />
                <p className="text-3xl mb-2">📷</p>
                <p className="text-sm font-medium text-gray-700">Click to upload photos</p>
                <p className="text-xs text-gray-400 mt-1">Add up to 5 photos (JPG, PNG)</p>
              </label>

              {photos.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {photos.map((f, i) => (
                    <img key={i} src={URL.createObjectURL(f)} alt=""
                      className="w-16 h-16 object-cover rounded-lg border border-primary" />
                  ))}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(s => s - 1)}
                  className="btn-outline flex-1 py-3">← Back</button>
                <button type="button" onClick={uploadPhotos} disabled={saving}
                  className="btn-primary flex-1 py-3">
                  {saving ? 'Uploading…' : photos.length ? 'Upload & Finish 🎉' : 'Skip & Finish →'}
                </button>
              </div>
            </div>
          ) : (
            /* Steps 0–3: react-hook-form */
            <form onSubmit={handleSubmit(saveStep)}>

              {/* ─── Step 0: Personal ─── */}
              {step === 0 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>First Name *</label>
                      <input className={inputCls} {...register('first_name', { required: 'Required' })} />
                      {errors.first_name && <p className={errCls}>{errors.first_name.message}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Last Name *</label>
                      <input className={inputCls} {...register('last_name', { required: 'Required' })} />
                      {errors.last_name && <p className={errCls}>{errors.last_name.message}</p>}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Date of Birth *</label>
                    <input type="date" className={inputCls} {...register('date_of_birth', { required: 'Required' })} />
                    {errors.date_of_birth && <p className={errCls}>{errors.date_of_birth.message}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Height (cm)</label>
                      <input type="number" placeholder="e.g. 165" className={inputCls} {...register('height_cm')} />
                    </div>
                    <div>
                      <label className={labelCls}>Complexion</label>
                      <select className={inputCls} {...register('complexion')}>
                        <option value="">Select</option>
                        <option value="fair">Fair</option>
                        <option value="wheatish">Wheatish</option>
                        <option value="dusky">Dusky</option>
                        <option value="dark">Dark</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Marital Status *</label>
                    <select className={inputCls} {...register('marital_status', { required: 'Required' })}>
                      <option value="never_married">Never Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>State *</label>
                    <select className={inputCls} {...register('state', { required: 'Required' })}>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Other">Other State</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>District *</label>
                    <select className={inputCls} {...register('district', { required: 'Required' })}>
                      <option value="">Select district</option>
                      {districts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {errors.district && <p className={errCls}>{errors.district.message}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>City / Town</label>
                    <input className={inputCls} placeholder="e.g. Vijayawada" {...register('city')} />
                  </div>

                  <div>
                    <label className={labelCls}>About Me</label>
                    <textarea rows={3} className={inputCls}
                      placeholder="Tell potential matches about yourself…"
                      {...register('about_me')} />
                  </div>
                </div>
              )}

              {/* ─── Step 1: Religion ─── */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Denomination *</label>
                    <select className={inputCls} {...register('denomination', { required: 'Please select your denomination' })}>
                      <option value="">Select denomination</option>
                      {DENOMINATIONS.map(d => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                    {errors.denomination && <p className={errCls}>{errors.denomination.message}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>Sub-denomination (optional)</label>
                    <input className={inputCls}
                      placeholder="e.g. Marthoma, AOG, Born Again…"
                      {...register('sub_denomination')} />
                  </div>
                </div>
              )}

              {/* ─── Step 2: Education & Job ─── */}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Education Level *</label>
                    <select className={inputCls} {...register('education_level', { required: 'Required' })}>
                      <option value="">Select</option>
                      <option value="below_10th">Below 10th</option>
                      <option value="10th">10th</option>
                      <option value="12th">12th / Intermediate</option>
                      <option value="diploma">Diploma</option>
                      <option value="bachelors">Bachelor's Degree</option>
                      <option value="masters">Master's Degree</option>
                      <option value="phd">PhD / Doctorate</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.education_level && <p className={errCls}>{errors.education_level.message}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>Field of Study</label>
                    <input className={inputCls} placeholder="e.g. Engineering, Arts, Commerce…" {...register('education_field')} />
                  </div>

                  <div>
                    <label className={labelCls}>College / University</label>
                    <input className={inputCls} placeholder="e.g. Andhra University" {...register('college')} />
                  </div>

                  <div>
                    <label className={labelCls}>Occupation *</label>
                    <select className={inputCls} {...register('occupation_type', { required: 'Required' })}>
                      <option value="employed">Employed</option>
                      <option value="business">Business / Self-employed</option>
                      <option value="government">Government Job</option>
                      <option value="student">Student</option>
                      <option value="not_working">Not Working</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Job Title</label>
                    <input className={inputCls} placeholder="e.g. Software Engineer" {...register('job_title')} />
                  </div>

                  <div>
                    <label className={labelCls}>Employer / Company</label>
                    <input className={inputCls} placeholder="e.g. TCS, Infosys, Govt…" {...register('employer')} />
                  </div>

                  <div>
                    <label className={labelCls}>Annual Income</label>
                    <select className={inputCls} {...register('annual_income')}>
                      <option value="">Prefer not to say</option>
                      <option value="below_2l">Below ₹2 Lakhs</option>
                      <option value="2l_5l">₹2 – 5 Lakhs</option>
                      <option value="5l_10l">₹5 – 10 Lakhs</option>
                      <option value="10l_20l">₹10 – 20 Lakhs</option>
                      <option value="above_20l">Above ₹20 Lakhs</option>
                    </select>
                  </div>
                </div>
              )}

              {/* ─── Step 3: Family ─── */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Father's Name</label>
                      <input className={inputCls} {...register('father_name')} />
                    </div>
                    <div>
                      <label className={labelCls}>Father's Occupation</label>
                      <input className={inputCls} placeholder="e.g. Farmer, Business…" {...register('father_occupation')} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Mother's Name</label>
                      <input className={inputCls} {...register('mother_name')} />
                    </div>
                    <div>
                      <label className={labelCls}>Mother's Occupation</label>
                      <input className={inputCls} placeholder="e.g. Homemaker, Teacher…" {...register('mother_occupation')} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Brothers</label>
                      <input type="number" min="0" max="10" className={inputCls} {...register('brothers')} />
                    </div>
                    <div>
                      <label className={labelCls}>Brothers Married</label>
                      <input type="number" min="0" max="10" className={inputCls} {...register('brothers_married')} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Sisters</label>
                      <input type="number" min="0" max="10" className={inputCls} {...register('sisters')} />
                    </div>
                    <div>
                      <label className={labelCls}>Sisters Married</label>
                      <input type="number" min="0" max="10" className={inputCls} {...register('sisters_married')} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Family Type</label>
                    <select className={inputCls} {...register('family_type')}>
                      <option value="">Select</option>
                      <option value="nuclear">Nuclear</option>
                      <option value="joint">Joint</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Family Status</label>
                    <select className={inputCls} {...register('family_status')}>
                      <option value="">Select</option>
                      <option value="middle_class">Middle Class</option>
                      <option value="upper_middle_class">Upper Middle Class</option>
                      <option value="rich">Rich</option>
                      <option value="affluent">Affluent</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-3 mt-6">
                {step > 0 && (
                  <button type="button" onClick={() => setStep(s => s - 1)}
                    className="btn-outline flex-1 py-3">← Back</button>
                )}
                <button type="submit" disabled={saving}
                  className="btn-primary flex-1 py-3">
                  {saving ? 'Saving…' : step === 3 ? 'Save & Continue →' : 'Save & Continue →'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
